import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CloudArrowUp, ShieldCheck, FloppyDisk, Trash, Info, CheckCircle, WarningCircle, PlugsConnected, ArrowSquareOut } from "@phosphor-icons/react";

// Module-scoped so framer-motion isn't handed a brand new object literal
// (and therefore treated as a changed prop) on every render of this page.
const PAGE_FADE_IN = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };


function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [libraryId, setLibraryId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/settings/bunny");
      setStatus(data);
    } catch {
      setStatus({ present: false, status: "not_connected" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const save = async () => {
    if (!libraryId.trim() || !apiKey.trim()) { toast.error("Enter both the Library ID and API key."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/settings/bunny", { library_id: libraryId.trim(), api_key: apiKey.trim() });
      setStatus(data);
      setLibraryId("");
      setApiKey("");
      toast.success("Bunny.net connected.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      const { data } = await api.delete("/settings/bunny");
      setStatus(data);
      toast.success("Bunny.net credentials removed.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/bunny/test");
      setStatus(data);
      if (data.success) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setTesting(false);
    }
  };

  const inputCls = "w-full bg-[#0A0A0A] border border-zinc-800 text-white px-3 py-2 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150 font-mono text-xs";

  return (
    <Layout>
      <motion.div {...PAGE_FADE_IN} className="mb-8">
        <p className="font-label text-xs text-[#CCFF00] mb-1">Settings</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white leading-none">Video hosting</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Upload new game videos directly on Bunny.net's own dashboard, then paste the link back into a game here to attach it — a fast CDN built for large match footage, without routing big uploads through this app. Connect your Bunny Stream library below so The Gaffer can verify pasted links, play them back, and pull clips for download/analysis.</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#121212] border border-zinc-800 p-6 rounded-sm" data-testid="bunny-settings-card">
          <h2 className="font-heading text-2xl text-white flex items-center gap-2 mb-4"><CloudArrowUp size={24} weight="fill" className="text-[#CCFF00]" /> Bunny.net Stream</h2>

          {loading ? (
            <p className="text-zinc-600 text-sm">Loading…</p>
          ) : (
            <div className="mb-5 flex flex-wrap items-center gap-2 text-sm" data-testid="bunny-status">
              {status?.status === "connected" && (
                <><CheckCircle size={18} weight="fill" className="text-[#32D74B]" /> <span className="text-[#32D74B]">Connected — Library {status.library_id}</span> <span className="text-zinc-600">· since {formatDate(status.connected_at)}</span></>
              )}
              {status?.status === "expired" && (
                <><WarningCircle size={18} weight="fill" className="text-[#FF9F0A]" /> <span className="text-[#FF9F0A]">Connection failed — {status.last_error || "check your credentials"}</span></>
              )}
              {(!status?.present) && (
                <span className="text-zinc-500">Not connected yet.</span>
              )}
              {status?.present && (
                <button onClick={testConnection} disabled={testing} data-testid="test-bunny-connection-btn" className="ml-auto flex items-center gap-1.5 text-zinc-400 hover:text-[#CCFF00] transition-colors duration-150 font-label text-xs">
                  <PlugsConnected size={14} weight="bold" /> {testing ? "Testing…" : "Test connection"}
                </button>
              )}
            </div>
          )}
          {status?.last_validated_at && (
            <p className="text-xs text-zinc-600 -mt-3 mb-4">Last checked {formatDate(status.last_validated_at)}</p>
          )}

          <label className="font-label text-xs text-zinc-400 block mb-2">Library ID</label>
          <input
            value={libraryId}
            onChange={(e) => setLibraryId(e.target.value)}
            data-testid="bunny-library-id-input"
            placeholder={status?.library_id ? `Currently ${status.library_id}` : "e.g. 123456"}
            className={inputCls + " mb-4"}
          />

          <label className="font-label text-xs text-zinc-400 block mb-2">API key</label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            data-testid="bunny-api-key-input"
            placeholder={status?.present ? "•••••••••••••••••••• (saved)" : "Your library's Stream API key"}
            className={inputCls}
          />
          <p className="text-xs text-zinc-600 mt-2 flex items-center gap-1"><ShieldCheck size={14} className="text-[#CCFF00]" /> Stored encrypted on the backend and never shown again — for your privacy we can't display a saved key back.</p>

          <div className="flex items-center gap-3 mt-5">
            <button onClick={save} disabled={saving} data-testid="save-bunny-btn" className="flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-6 py-3 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save & connect"}
            </button>
            {status?.present && (
              <button onClick={clear} disabled={saving} data-testid="remove-bunny-btn" className="flex items-center gap-2 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 font-label text-xs">
                <Trash size={16} weight="bold" /> Remove credentials
              </button>
            )}
          </div>
        </div>

        <div className="bg-[#121212] border border-zinc-800 p-6 rounded-sm" data-testid="bunny-help">
          <h2 className="font-heading text-xl text-white flex items-center gap-2 mb-4"><Info size={20} weight="fill" className="text-[#CCFF00]" /> Where to find these</h2>
          <ol className="text-sm text-zinc-400 space-y-3 list-decimal list-inside">
            <li>Sign in to <a href="https://dash.bunny.net" target="_blank" rel="noopener noreferrer" className="text-white inline-flex items-center gap-1 hover:text-[#CCFF00]">Bunny.net's dashboard <ArrowSquareOut size={12} /></a> and open your Stream video library.</li>
            <li>Open the library's <span className="text-white">API</span> tab — copy the <span className="text-white">Library ID</span> and <span className="text-white">Video Library API Key</span> (not your account API key).</li>
            <li>Paste both into the fields on the left and click <span className="text-white">Save & connect</span> — we'll verify them immediately.</li>
            <li>In the same library's settings, enable <span className="text-white">"Keep original files"</span> — this is required so clip downloads and highlight reels can trim the real source video.</li>
            <li>To attach a game video: upload it on Bunny's dashboard first, then paste the Video URL (or Video ID) it gives you into that game's page here.</li>
          </ol>
          <div className="mt-5 border-t border-zinc-800 pt-4 text-xs text-zinc-600 space-y-2">
            <p><span className="text-zinc-400">Note:</span> existing games with a YouTube link or a locally-uploaded video keep working exactly as before — this only applies to new videos attached going forward.</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
